import { useMemo, useRef } from "react";

import {
  ContentType,
  DataBlockChangesetResponse,
  FieldType,
} from "@/redux/datablocks/types";
import { getQueryPropsForContentType } from "@/domains/datablocks/fields/contentTypeConfig";
import {
  RelatedDataItem,
  ScaleRelation,
  UseHistoryRelatedDataReturn,
} from "../types";

const EMPTY_IDS: number[] = [];

const useContentTypeData = (ids: number[], contentType: ContentType) => {
  const queryConfig = getQueryPropsForContentType(contentType);
  return queryConfig.useQuery(
    { ids, pagination: "off" as const },
    { skip: ids.length === 0 },
  );
};

const extractRelationIds = (
  relations: number[] | ScaleRelation[] | null,
): number[] => {
  if (!relations) return [];
  const ids: number[] = [];
  for (const item of relations) {
    if (typeof item === "number") {
      ids.push(item);
    } else {
      ids.push(item.objectId);
      if (item.scale) ids.push(item.scale);
    }
  }
  return ids;
};

const collectRelationIds = (
  changesets: DataBlockChangesetResponse[],
): Record<string, number[]> => {
  const ids: Record<string, number[]> = {};
  for (const ct of Object.values(ContentType)) {
    ids[ct] = [];
  }

  for (const cs of changesets) {
    for (const change of cs.changes) {
      if (
        !change.dataField ||
        change.dataField.type !== FieldType.SELECT ||
        !change.dataField.contentType
      ) {
        continue;
      }
      const ct = change.dataField.contentType;
      if (!ids[ct]) ids[ct] = [];
      ids[ct].push(...extractRelationIds(change.oldRelations));
      ids[ct].push(...extractRelationIds(change.newRelations));
    }
  }

  for (const key of Object.keys(ids)) {
    ids[key] = Array.from(new Set(ids[key]));
  }

  return ids;
};

export const useHistoryRelatedData = (
  newChangesets: DataBlockChangesetResponse[],
): UseHistoryRelatedDataReturn => {
  const accumulatedLookupRef = useRef<Map<string, RelatedDataItem>>(new Map());

  const idsByContentType = useMemo(
    () => collectRelationIds(newChangesets),
    [newChangesets],
  );

  const optionsResult = useContentTypeData(
    idsByContentType[ContentType.Options] ?? EMPTY_IDS,
    ContentType.Options,
  );
  const usersResult = useContentTypeData(
    idsByContentType[ContentType.Users] ?? EMPTY_IDS,
    ContentType.Users,
  );
  const filesResult = useContentTypeData(
    idsByContentType[ContentType.Files] ?? EMPTY_IDS,
    ContentType.Files,
  );
  const subjectsResult = useContentTypeData(
    idsByContentType[ContentType.Subjects] ?? EMPTY_IDS,
    ContentType.Subjects,
  );
  const chaptersResult = useContentTypeData(
    idsByContentType[ContentType.Chapters] ?? EMPTY_IDS,
    ContentType.Chapters,
  );
  const languagesResult = useContentTypeData(
    idsByContentType[ContentType.Languages] ?? EMPTY_IDS,
    ContentType.Languages,
  );
  const countriesResult = useContentTypeData(
    idsByContentType[ContentType.Countries] ?? EMPTY_IDS,
    ContentType.Countries,
  );

  const results = [
    { ct: ContentType.Options, ...optionsResult },
    { ct: ContentType.Users, ...usersResult },
    { ct: ContentType.Files, ...filesResult },
    { ct: ContentType.Subjects, ...subjectsResult },
    { ct: ContentType.Chapters, ...chaptersResult },
    { ct: ContentType.Languages, ...languagesResult },
    { ct: ContentType.Countries, ...countriesResult },
  ];

  const isLoading = results.some((r) => r.isLoading);

  const lookup = useMemo(() => {
    for (const { ct, data } of results) {
      const items = Array.isArray(data) ? data : (data?.results ?? []);

      for (const item of items) {
        accumulatedLookupRef.current.set(
          `${ct}:${item.id}`,
          item as RelatedDataItem,
        );
      }
    }
    return new Map(accumulatedLookupRef.current);
  }, [
    optionsResult.data,
    usersResult.data,
    filesResult.data,
    subjectsResult.data,
    chaptersResult.data,
    languagesResult.data,
    countriesResult.data,
  ]);

  return { lookup, isLoading };
};
